<script setup lang="ts">
  // External
  import dayjs from 'dayjs';
  import { computed, onBeforeMount, ref } from 'vue';

  // Workspace
  import type { Sms, SMSThread } from '@stuut/api-sdk/models';
  import { SMSStatus } from '@stuut/api-sdk/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';
  import { Badge, DirectionBadge } from '@ui/components/badge';
  import { Card, CardContent, CardHeader, CardTitle } from '@ui/components/card';
  import { Skeleton } from '@ui/components/skeleton';
  import { Textarea } from '@ui/components/textarea';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { sdk } from '@/sdk';

  import SendSmsButton from './SendSmsButton.vue';

  interface Props {
    draftSms?: Sms | null;
    thread: SMSThread;
  }

  const props = defineProps<Props>();
  const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'sent'): void;
  }>();

  const messages = ref<Sms[]>([]);
  const loading = ref(false);
  const draftBody = ref(props.draftSms?.body ?? '');

  const fetchMessages = async () => {
    loading.value = true;
    try {
      const response = await sdk.smsThreads.listMessages({ smsThreadId: props.thread.id });
      messages.value = response || [];
    } catch (err) {
      console.error('Error fetching SMS messages:', err);
    } finally {
      loading.value = false;
    }
  };

  // Filter out draft messages from the display list
  const displayMessages = computed(() =>
    messages.value.filter((m) => m.status !== SMSStatus.Draft),
  );

  const fromNumberFormatted = computed(() => formatPhoneNumber(props.thread.fromNumber));
  const toNumberFormatted = computed(() => formatPhoneNumber(props.thread.toNumber));

  onBeforeMount(async () => {
    await fetchMessages();
  });
</script>

<template>
  <div class="h-full overflow-y-auto p-6 grid gap-y-4">
    <!-- Thread Info Card -->
    <Card>
      <CardHeader>
        <div class="flex justify-between items-start">
          <CardTitle> SMS Thread </CardTitle>
        </div>
      </CardHeader>

      <CardContent class="space-y-3">
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div class="text-muted-foreground text-xs">From</div>

            <div class="font-medium">
              {{ fromNumberFormatted || thread.fromNumber }}
            </div>
          </div>

          <div>
            <div class="text-muted-foreground text-xs">To</div>

            <div class="font-medium">
              {{ toNumberFormatted || thread.toNumber }}
            </div>
          </div>

          <div>
            <div class="text-muted-foreground text-xs">Last Inbound</div>

            <div>
              <Timestamp
                :time="thread.lastInboundAt ? dayjs(thread.lastInboundAt).utc(true) : null"
                with-conversion-table
                local
                show-from-now
              />
            </div>
          </div>

          <div>
            <div class="text-muted-foreground text-xs">Last Outbound</div>

            <div>
              <Timestamp
                :time="thread.lastOutboundAt ? dayjs(thread.lastOutboundAt).utc(true) : null"
                with-conversion-table
                local
                show-from-now
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <!-- Draft Editor Card -->
    <Card v-if="draftSms">
      <CardHeader>
        <CardTitle> Draft SMS </CardTitle>
      </CardHeader>

      <CardContent class="space-y-4">
        <Textarea
          v-model="draftBody"
          placeholder="Type your SMS message..."
          class="min-h-[120px] resize-none"
        />

        <div class="flex justify-end">
          <SendSmsButton :sms-id="draftSms.id" :body="draftBody" @sent="emits('sent')" />
        </div>
      </CardContent>
    </Card>

    <!-- Messages Card -->
    <Card>
      <CardHeader>
        <CardTitle> Messages ({{ displayMessages.length }}) </CardTitle>
      </CardHeader>

      <CardContent>
        <div v-if="loading" class="space-y-4">
          <div class="flex flex-col p-3 rounded-sm ml-0 mr-20">
            <Skeleton class="w-16 h-5 mb-2" />

            <Skeleton class="w-full h-4 mb-1" />

            <Skeleton class="w-3/4 h-4 mb-2" />

            <Skeleton class="w-24 h-3" />
          </div>

          <div class="flex flex-col p-3 rounded-sm bg-primary/10 ml-20 mr-0 items-end">
            <Skeleton class="w-16 h-5 mb-2" />

            <Skeleton class="w-full h-4 mb-1" />

            <Skeleton class="w-1/2 h-4 mb-2" />

            <Skeleton class="w-24 h-3" />
          </div>

          <div class="flex flex-col p-3 rounded-sm ml-0 mr-20">
            <Skeleton class="w-16 h-5 mb-2" />

            <Skeleton class="w-2/3 h-4 mb-2" />

            <Skeleton class="w-24 h-3" />
          </div>
        </div>

        <div v-else-if="!displayMessages.length" class="text-center py-8 text-muted-foreground">
          No messages found
        </div>

        <div v-else class="space-y-4">
          <div
            v-for="message in displayMessages"
            :key="message.id"
            class="flex flex-col p-3 rounded-sm"
            :class="[
              message.direction === 'inbound'
                ? 'ml-0 mr-20 items-start'
                : 'bg-primary/10 ml-20 mr-0 items-end',
            ]"
          >
            <div
              class="flex mb-2 gap-2"
              :class="[message.direction === 'outbound' ? 'justify-end' : 'justify-start']"
            >
              <DirectionBadge
                :direction="message.direction === 'inbound' ? 'inbound' : 'outbound'"
                with-label
              />
              <Badge
                v-if="
                  message.status === SMSStatus.Failed || message.status === SMSStatus.Undelivered
                "
                variant="destructive"
                size="xs"
              >
                Failed
              </Badge>

              <Tooltip
                v-if="
                  message.direction === 'outbound' &&
                  (message.status === SMSStatus.Failed || message.status === SMSStatus.Undelivered)
                "
                :delay-duration="0"
              >
                <TooltipTrigger as-child>
                  <Badge variant="blue" size="xs">
                    <i class="fa far fa-info-circle" />
                  </Badge>
                </TooltipTrigger>

                <TooltipContent side="top" class="max-w-sm text-left">
                  {{ message.errorMessage?.trim() || 'Message delivery failed.' }}
                </TooltipContent>
              </Tooltip>
            </div>

            <div class="text-sm whitespace-pre-wrap">{{ message.body }}</div>

            <Timestamp
              class="mt-1 text-xs text-muted-foreground"
              :time="dayjs(message.sentAt || message.createdAt)"
              with-conversion-table
              local
            />
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<style scoped></style>
